import { Clock, ShieldAlert, ShieldCheck, type LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import type { VerificationTranslations } from "@/shared/lang/interfaces/verification";
import useTranslations from "@/shared/lang/useTranslations";

import {
  CAPABILITY_LABEL_KEYS,
  NEXT_STEP_CTA_KEYS,
  ONBOARDING_VERIFICATION_ROUTE,
  resolveRejectionDescription,
} from "../constants/verification.constants";
import { useOrganizationVerificationStatus } from "../hooks/useOrganizationVerificationStatus";
import type { KycStatus } from "../types/verification-api.types";
import { KycStatusPill } from "./KycStatusPill";

interface BannerMeta {
  variant: "secondary" | "warning" | "destructive";
  icon: LucideIcon;
  titleKey: keyof VerificationTranslations;
  /** `null` — the description is the rejection reason. */
  descriptionKey: keyof VerificationTranslations | null;
}

const META_BY_STATUS: Record<KycStatus, BannerMeta> = {
  PENDING: {
    variant: "warning",
    icon: ShieldCheck,
    titleKey: "onboarding_title",
    descriptionKey: "onboarding_description",
  },
  EXPIRED: {
    variant: "warning",
    icon: ShieldAlert,
    titleKey: "banner_expired_title",
    descriptionKey: "banner_expired_description",
  },
  IN_REVIEW: {
    variant: "secondary",
    icon: Clock,
    titleKey: "banner_in_review_title",
    descriptionKey: "banner_in_review_description",
  },
  REJECTED: {
    variant: "destructive",
    icon: ShieldAlert,
    titleKey: "banner_rejected_title",
    descriptionKey: null,
  },
  // Unreachable while `shouldVerify` requires a non-verified state; kept so the record is exhaustive.
  VERIFIED: {
    variant: "secondary",
    icon: ShieldCheck,
    titleKey: "onboarding_title",
    descriptionKey: "onboarding_description",
  },
};

/** The section of Settings that always shows the verification, its data and what it unlocks. */
const SETTINGS_VERIFICATION_ROUTE = "/app/settings?section=verificacion";

/**
 * Status-aware onboarding nudge on the Wallets home for organization members whose account (KYC or KYB) is
 * not verified yet: the current status, what it blocks, and the next step. Renders nothing for internal
 * staff, for verified organizations, while loading, or when the status can't be resolved. The server is the
 * source of truth — the real gate is the backend's 403 on payouts.
 */
export function OrganizationVerificationBanner() {
  const { t } = useTranslations("verification");
  const navigate = useNavigate();
  const { shouldVerify, kycStatus, nextStep, blockedCapabilities, rejectionReason } =
    useOrganizationVerificationStatus();

  if (!shouldVerify) return null;

  const status: KycStatus = kycStatus ?? "PENDING";
  const meta = META_BY_STATUS[status];
  const description = meta.descriptionKey
    ? t(meta.descriptionKey)
    : resolveRejectionDescription(rejectionReason, t);
  const ctaKey = NEXT_STEP_CTA_KEYS[nextStep];
  const blockedLabels = blockedCapabilities.map((capability) => t(CAPABILITY_LABEL_KEYS[capability]));

  return (
    <ColocaAlert
      variant={meta.variant}
      icon={meta.icon}
      heading={
        <span className="flex flex-wrap items-center gap-2">
          {t(meta.titleKey)}
          <KycStatusPill status={status} />
        </span>
      }
      text={
        <span className="block space-y-1">
          <span className="block">{description}</span>
          {blockedLabels.length > 0 && (
            <span className="block">
              {t("banner_blocked_prefix")} <strong>{blockedLabels.join(" · ")}</strong>
            </span>
          )}
        </span>
      }
      actions={
        <>
          {ctaKey && (
            <ColocaButton size="sm" variant="primary" onClick={() => navigate(ONBOARDING_VERIFICATION_ROUTE)}>
              {t(ctaKey)}
            </ColocaButton>
          )}
          <ColocaButton size="sm" variant="ghost" onClick={() => navigate(SETTINGS_VERIFICATION_ROUTE)}>
            {t("banner_view_details")}
          </ColocaButton>
        </>
      }
    />
  );
}
